// @ts-strict-ignore
import {AttrsSchema, Component, h} from 'panel';
import MarkdownIt from 'markdown-it';
import {selx} from 'clsx';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {withStylesPanel} from 'common/util/panel';

import {MarkdownAttrs, MarkdownJsxProps} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

export class MarkdownImpl extends Component<EmptyObj, MarkdownAttrs> {
  declare converter: MarkdownIt;

  static get attrsSchema(): AttrsSchema<MarkdownAttrs> {
    return {
      ...super.attrsSchema,
      text: {type: `string`, description: `Markdown formatted text to be processed and rendered as HTML`},
      'allow-breaks': {type: `boolean`, description: `Allow new line characters to be treated as breaks`},
      linkify: {type: `boolean`, description: `Automatically convert things that look like links into links`},
      'multi-line': {type: `boolean`, description: `Allow multiple lines of markdown`},
      truncate: {type: `boolean`, description: `Should text be truncated`},
    };
  }

  get config() {
    return {
      template: () => h(`div${selx(styles.container, {[styles.truncate]: this.attr(`truncate`)})}`),
      defaultState: {},
      helpers: {},
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (!this.initialized) {
      return;
    }
    if (attr === `text`) {
      // The markdown library we're using handles the sanitization for us.
      // eslint-disable-next-line no-unsanitized/property
      this.el.querySelector(selx(styles.container)).innerHTML = this.getMarkup();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // The markdown library we're using handles the sanitization for us.
    // eslint-disable-next-line no-unsanitized/property
    this.el.querySelector(selx(styles.container)).innerHTML = this.getMarkup();
  }

  constructor() {
    super();

    this.converter = new MarkdownIt();
    // adds blank targets to all links
    this.converter.renderer.rules.link_open = function (tokens, idx, options, _env, self) {
      if (tokens[idx].tag === `a`) {
        tokens[idx].attrPush([`target`, `_blank`]);
        tokens[idx].attrPush([`rel`, `noreferrer noopener`]);
      }
      // pass token to default renderer.
      return self.renderToken(tokens, idx, options);
    };
  }

  getMarkup() {
    if (this.attr(`allow-breaks`) || this.attr(`linkify`)) {
      this.converter.set({breaks: this.attr(`allow-breaks`), linkify: this.attr(`linkify`)});
    }
    const markdownText = this.attr(`text`);

    if (this.attr(`multi-line`)) {
      return this.converter.render(markdownText).trim();
    }

    return this.converter.renderInline(markdownText).trim();
  }
}

export const Markdown = withStylesPanel(MarkdownImpl, css);

export const MarkdownEl = defineAndMakeJsxFactory<MarkdownJsxProps>(`mp-markdown`, Markdown);
export const MarkdownReact = panel2react<MarkdownJsxProps>(`mp-markdown`, Markdown);
